import React, { useState, useEffect } from 'react';
import { useMsalAuth } from './MsalProvider';
import OneDriveService from '../utils/oneDriveService';
import { FolderOpen, ChevronLeft, ChevronRight, File, Folder, CheckSquare, Square, ArrowUpRight, X } from 'lucide-react';

const OneDrivePicker = ({ onSelect, acceptedFileTypes = [], multiSelect = false, maxSelections = Infinity, id, ref, customButtonRender }) => {
  const { instance, isAuthenticated, isInitialized, forceNewLogin, logout, clearAuthState } = useMsalAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([{ id: null, name: 'Root' }]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Initialize OneDrive service only when needed, not at component mount
  const [oneDriveService, setOneDriveService] = useState(null);

  // Initialize service on authentication change
  useEffect(() => {
    // Only initialize service when explicitly needed (in loadFiles), not on mount
    // This prevents automatic authentication
  }, []);

  // Open picker only when explicitly clicked - always force a new login with account selection
  const openPicker = async () => {
    try {
      // Clear any OneDrive-specific tokens before proceeding
      console.log('Clearing OneDrive tokens before login...');
      sessionStorage.removeItem('oneDriveToken');
      localStorage.removeItem('oneDriveToken');
      
      // Force a fresh login with account selection dialog and explicit consent
      console.log('Forcing login with account selection dialog...');
      const success = await forceNewLogin();

      if (!success) {
        console.error('Login failed or was cancelled');
        // Ensure we're fully logged out
        try {
          console.log('Login cancelled, performing logout');
          await logout();
        } catch (logoutError) {
          console.error('Error during logout after popup close:', logoutError);
        }
        onSelect({ action: 'cancel' });
        return;
      }

      // Give a moment for authentication to complete
      await new Promise(resolve => setTimeout(resolve, 300));

      // Initialize OneDriveService after fresh login
      try {
        console.log('Creating OneDrive service instance after fresh login');
        const service = new OneDriveService(instance);
        setOneDriveService(service);
        
        // Explicitly test token acquisition with consent prompt
        try {
          const account = service.getActiveAccount();
          if (account) {
            console.log('Testing token acquisition without explicit prompt...');
            // Try to get a token with popup without specifying a prompt value
            // Microsoft will automatically show consent if needed
            const token = await instance.acquireTokenPopup({
              scopes: [
                "User.Read",
                "Files.Read",
                "Files.Read.All",
                "Files.ReadWrite",
                "Files.ReadWrite.All",
                "Sites.Read.All"
              ],
              account: account
              // Don't specify prompt - Microsoft will show consent dialog if needed
            });
            
            if (token && token.accessToken) {
              console.log('Successfully acquired token with consent');
              sessionStorage.setItem('oneDriveToken', token.accessToken);
            }
          }
        } catch (tokenError) {
          console.error('Error getting token with consent:', tokenError);
          // Continue anyway - we'll try again during file loading
        }
        
        // Wait a moment for the service to be set in state
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (serviceError) {
        console.error('Failed to create OneDrive service:', serviceError);
        onSelect({ action: 'error', message: 'OneDrive service initialization failed. Please try again.' });
        return;
      }

      // Once logged in, open the picker UI
      setIsOpen(true);
      loadFiles(null); // Load root folder
    } catch (error) {
      console.error('Error opening OneDrive picker:', error);
      onSelect({ action: 'error', message: 'Error opening OneDrive: ' + error.message });
    }
  };

  // Load files and folders from OneDrive
  const loadFiles = async (folderId) => {
    setLoading(true);
    try {
      // Make sure we're initialized and authenticated
      if (!instance || !isInitialized) {
        console.error('MSAL not initialized when trying to load files');
        throw new Error('Service not available. Please refresh and try again.');
      }
      
      if (!isAuthenticated) {
        // Don't show error immediately, try to get accounts first
        const accounts = instance.getAllAccounts();
        if (accounts.length === 0) {
          console.error('No accounts found after authentication');
          throw new Error('No account selected. Please try again.');
        }
      }
      
      // Create a new service using the current instance
      console.log('Creating OneDrive service to load files');
      try {
        const service = new OneDriveService(instance);
        setOneDriveService(service);
        
        // Get a token to verify authentication
        const token = await service.getTokenSilently();
        if (!token) {
          throw new Error('Failed to get access token');
        }
        
        // Load the files with the token
        console.log('Loading files from OneDrive folder:', folderId);
        const items = await service.getDriveItems(folderId);
        handleLoadedItems(items, folderId);
      } catch (serviceError) {
        console.error('Error with OneDrive service:', serviceError);
        throw serviceError;
      }
    } catch (error) {
      console.error('Error loading OneDrive files:', error);
      setFiles([]);
      setFolders([]);
      // Show error message
      if (error.message) {
        onSelect({ action: 'error', message: 'Error: ' + error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to process loaded items
  const handleLoadedItems = (items, folderId) => {
    console.log('OneDrive items loaded:', items);
    if (!items || !items.value) {
      console.error('Invalid items response:', items);
      setFolders([]);
      setFiles([]);
      setCurrentFolder(folderId);
      return;
    }

    // Separate files and folders
    const folderItems = items.value.filter(item => item.folder);
    const fileItems = items.value.filter(item => item.file);

    // Filter files by accepted types if provided
    const filteredFiles = acceptedFileTypes.length > 0
      ? fileItems.filter(item => {
        const extension = item.name.split('.').pop().toLowerCase();
        return acceptedFileTypes.includes(extension);
      })
      : fileItems;

    setFolders(folderItems);
    setFiles(filteredFiles);
    setCurrentFolder(folderId);
    console.log(`Loaded ${folderItems.length} folders and ${filteredFiles.length} files`);
  };

  // Navigate to a folder
  const navigateToFolder = (folder, index) => {
    // If clicking on a folder in the path, navigate to that folder
    if (index !== undefined) {
      // Truncate the path up to the clicked folder
      setFolderPath(folderPath.slice(0, index + 1));
      loadFiles(folder.id);
    }
    // Otherwise, navigate to a new folder
    else {
      setFolderPath([...folderPath, { id: folder.id, name: folder.name }]);
      loadFiles(folder.id);
    }
  };

  // Navigate to parent folder
  const navigateUp = () => {
    if (folderPath.length > 1) {
      const newPath = folderPath.slice(0, -1);
      setFolderPath(newPath);
      loadFiles(newPath[newPath.length - 1].id);
    }
  };

  // Toggle file selection
  const toggleFileSelection = (file) => {
    if (!multiSelect) {
      setSelectedFiles([file]);
    } else {
      const isSelected = selectedFiles.some(f => f.id === file.id);
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter(f => f.id !== file.id));
      } else {
        // Check if adding this file would exceed the maximum selection limit
        if (selectedFiles.length >= maxSelections) {
          // If already at max, don't add more files
          return;
        }
        setSelectedFiles([...selectedFiles, file]);
      }
    }
  };

  // Confirm selection and close picker
  const confirmSelection = async () => {
    // Convert selected files to the format expected by the parent component
    const processedFiles = selectedFiles.map(file => ({
      id: file.id,
      name: file.name,
      mimeType: file.file.mimeType,
      size: file.size,
      webUrl: file.webUrl,
      lastModifiedDateTime: file.lastModifiedDateTime,
    }));

    // Call the onSelect callback
    onSelect({
      action: 'picked',
      docs: processedFiles,
    });

    // Close the picker
    setIsOpen(false);
    setSelectedFiles([]);
  };

  // Cancel and close picker
  const cancelSelection = () => {
    onSelect({ action: 'cancel' });
    setIsOpen(false);
    setSelectedFiles([]);
  };

  // Button to trigger the picker
  const PickerButton = () => {
    // If a custom button render function is provided, use it
    if (customButtonRender) {
      return customButtonRender(openPicker);
    }

    // Default button
    return (
      <button
        id="onedrive-picker-btn"
        onClick={openPicker}
        className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium flex items-center gap-2"
      >
        <FolderOpen className="w-4 h-4" />
        Connect with OneDrive
      </button>
    );
  };

  // Render the picker modal
  if (!isOpen) {
    return <PickerButton />;
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center" style={{ marginTop: '60px' }}>
      <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[70vh] flex flex-col relative z-50">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Select from OneDrive</h2>
          <button
            onClick={cancelSelection}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Folder navigation */}
        <div className="px-6 py-2 border-b flex items-center">
          <button
            onClick={navigateUp}
            disabled={folderPath.length <= 1}
            className="p-1 mr-2 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-1 text-sm overflow-x-auto">
            {folderPath.map((folder, index) => (
              <React.Fragment key={folder.id || 'root'}>
                {index > 0 && <ChevronRight className="w-3 h-3 text-gray-400" />}
                <button
                  onClick={() => navigateToFolder(folder, index)}
                  className={`px-2 py-1 rounded hover:bg-gray-100 ${index === folderPath.length - 1 ? 'font-medium' : ''
                    }`}
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* File browser */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-yellow-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Folders */}
              {folders.map(folder => (
                <div
                  key={folder.id}
                  onClick={() => navigateToFolder(folder)}
                  className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <Folder className="w-10 h-10 text-yellow-500" />
                  <p className="text-sm text-center truncate w-full">{folder.name}</p>
                </div>
              ))}

              {/* Files */}
              {files.map(file => (
                <div
                  key={file.id}
                  onClick={() => toggleFileSelection(file)}
                  className={`flex flex-col items-center gap-2 p-4 border rounded-lg cursor-pointer ${selectedFiles.some(f => f.id === file.id)
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-gray-200 hover:bg-gray-50'
                    }`}
                >
                  <div className="relative">
                    <File className="w-10 h-10 text-blue-500" />
                    <div className="absolute -top-2 -right-2">
                      {selectedFiles.some(f => f.id === file.id) ? (
                        <CheckSquare className="w-4 h-4 text-yellow-500" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-center truncate w-full">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {file.size < 1024 * 1024
                      ? `${(file.size / 1024).toFixed(1)} KB`
                      : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                  </p>
                </div>
              ))}

              {folders.length === 0 && files.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500">No files or folders found</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer with actions */}
        <div className="px-6 py-4 border-t flex items-center justify-between">
          <div className="text-sm">
            {selectedFiles.length > 0
              ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected ${multiSelect && maxSelections < Infinity ? `(max ${maxSelections})` : ''}`
              : `No files selected ${multiSelect && maxSelections < Infinity ? `(max ${maxSelections})` : ''}`}
          </div>
          <div className="flex gap-3">
            <button
              onClick={cancelSelection}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmSelection}
              disabled={selectedFiles.length === 0}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:bg-yellow-300"
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OneDrivePicker;